import { useCallback, useEffect, useRef, useState } from "react";
import type { RotateAgentIdentityPayload } from "../../api/admin-agent-identity-rotation";
import { t } from "../../i18n";

type FormState = {
  readonly new_ik_x25519_public_key: string;
  readonly new_ik_ed25519_public_key: string;
  readonly new_spk_id: string;
  readonly new_spk_public_key: string;
  readonly new_spk_signature: string;
  readonly new_spk_expires_at: string;
  readonly identity_binding_proof: string;
  readonly justification: string;
};

type FormKey = keyof FormState;

type Field = {
  readonly key: FormKey;
  readonly label: string;
  readonly multiline?: boolean;
};

const EMPTY_FORM: FormState = {
  new_ik_x25519_public_key: "",
  new_ik_ed25519_public_key: "",
  new_spk_id: "",
  new_spk_public_key: "",
  new_spk_signature: "",
  new_spk_expires_at: "",
  identity_binding_proof: "",
  justification: "",
};

const FIELDS: ReadonlyArray<Field> = [
  { key: "new_ik_x25519_public_key", label: "new_ik_x25519_public_key" },
  { key: "new_ik_ed25519_public_key", label: "new_ik_ed25519_public_key" },
  { key: "new_spk_id", label: "new_spk_id" },
  { key: "new_spk_public_key", label: "new_spk_public_key" },
  { key: "new_spk_signature", label: "new_spk_signature" },
  { key: "new_spk_expires_at", label: "new_spk_expires_at" },
  { key: "identity_binding_proof", label: "identity_binding_proof" },
  { key: "justification", label: "justification", multiline: true },
];

export function AgentIdentityRotationDialog({
  open,
  agentName,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  agentName: string;
  onConfirm: (payload: RotateAgentIdentityPayload) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canConfirm = isValidForm(form);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = useCallback((): void => {
    setForm(EMPTY_FORM);
    onCancel();
  }, [onCancel]);

  const submit = useCallback((): void => {
    if (!canConfirm) return;
    onConfirm(toPayload(form));
    setForm(EMPTY_FORM);
  }, [canConfirm, form, onConfirm]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="identity-rotation-title"
      style={styles.dialog}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <div style={styles.content}>
        <h2 id="identity-rotation-title" style={styles.title}>
          {t("agent_identity_rotation.title")}
        </h2>
        <p style={styles.description}>
          {t("agent_identity_rotation.description", { name: agentName })}
        </p>
        <div style={styles.grid}>
          {FIELDS.map((field) => (
            <RotationField
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={(value) => setForm({ ...form, [field.key]: value })}
            />
          ))}
        </div>
        <div style={styles.actions}>
          <button type="button" onClick={close} style={styles.cancelBtn}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={submit}
            style={{
              ...styles.confirmBtn,
              ...(canConfirm ? undefined : styles.confirmBtnDisabled),
            }}
          >
            {t("agent_identity_rotation.confirm")}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function RotationField({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const inputId = `identity-rotation-${field.key}`;
  return (
    <label htmlFor={inputId} style={styles.field}>
      <span style={styles.label}>
        {t(`agent_identity_rotation.${field.key}`)}
      </span>
      {field.multiline ? (
        <textarea
          id={inputId}
          value={value}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
          style={styles.textarea}
        />
      ) : (
        <input
          id={inputId}
          type={field.key === "new_spk_id" ? "number" : "text"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={styles.input}
        />
      )}
    </label>
  );
}

function isValidForm(form: FormState): boolean {
  const spkId = Number.parseInt(form.new_spk_id, 10);
  return (
    Object.values(form).every((value) => value.trim().length > 0) &&
    Number.isInteger(spkId) &&
    spkId >= 0 &&
    form.justification.trim().length >= 20
  );
}

function toPayload(form: FormState): RotateAgentIdentityPayload {
  return {
    new_ik_x25519_public_key: form.new_ik_x25519_public_key.trim(),
    new_ik_ed25519_public_key: form.new_ik_ed25519_public_key.trim(),
    new_spk_id: Number.parseInt(form.new_spk_id, 10),
    new_spk_public_key: form.new_spk_public_key.trim(),
    new_spk_signature: form.new_spk_signature.trim(),
    new_spk_expires_at: form.new_spk_expires_at.trim(),
    identity_binding_proof: form.identity_binding_proof.trim(),
    justification: form.justification.trim(),
  };
}

const styles: Record<string, React.CSSProperties> = {
  dialog: {
    border: "none",
    borderRadius: 8,
    padding: 0,
    maxWidth: 760,
    width: "min(760px, calc(100vw - 32px))",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
  },
  content: { padding: 24 },
  title: { fontSize: 18, fontWeight: 600, marginBottom: 8 },
  description: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    lineHeight: 1.6,
    marginBottom: 16,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    fontWeight: 600,
  },
  input: {
    padding: "8px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    fontFamily: "var(--font-mono)",
  },
  textarea: {
    padding: "8px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    resize: "vertical",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
  cancelBtn: {
    padding: "8px 16px",
    fontSize: 14,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
  confirmBtn: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 6,
    border: "none",
    background: "var(--color-danger)",
    color: "#ffffff",
  },
  confirmBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
};
